import { useState } from "react";

interface BatchToolsModalProps {
  totalCount: number;
  selectedIds: string[];
  onApplyTime: (ids: string[], times: string[]) => void;
  onApplyPublisher: (ids: string[], publishers: string[]) => void;
  onClose: () => void;
}

const PUBLISHER_POOL = [
  "张伟", "李娜", "王芳", "刘洋", "陈静", "赵磊", "孙丽", "周强",
  "吴敏", "郑超", "冯霞", "蒋涛", "沈婷", "韩杰", "杨帆", "朱燕",
  "秦浩", "许晨", "何雪", "林峰", "曹阳", "彭欣", "董辉", "谢梅",
  "宋博", "唐宁", "潘磊", "江雨", "史伟", "薛丹",
];

function formatDateOnly(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${formatDateOnly(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function getDefaultDateRange() {
  const today = new Date();
  const tenDaysAgo = new Date(today);
  tenDaysAgo.setDate(today.getDate() - 10);
  return { start: formatDateOnly(tenDaysAgo), end: formatDateOnly(today) };
}

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export default function BatchToolsModal({
  totalCount,
  selectedIds,
  onApplyTime,
  onApplyPublisher,
  onClose,
}: BatchToolsModalProps) {
  const [activeTab, setActiveTab] = useState<"time" | "publisher">("time");

  // Time settings
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [fillMode, setFillMode] = useState<"random" | "sequential">("random");
  const [applyScope, setApplyScope] = useState<"all" | "selected">("all");
  const [timePreview, setTimePreview] = useState<string[]>([]);
  const [timePreviewGenerated, setTimePreviewGenerated] = useState(false);

  // Publisher settings
  const [pubScope, setPubScope] = useState<"all" | "selected">("all");
  const [customPublishers, setCustomPublishers] = useState("");
  const [pubPreview, setPubPreview] = useState<string[]>([]);
  const [pubPreviewGenerated, setPubPreviewGenerated] = useState(false);

  const targetCount =
    applyScope === "selected" && selectedIds.length > 0
      ? selectedIds.length
      : totalCount;

  const pubTargetCount =
    pubScope === "selected" && selectedIds.length > 0
      ? selectedIds.length
      : totalCount;

  function generateTimes(): string[] {
    const start = new Date(startDate + "T00:00:00").getTime();
    const end = new Date(endDate + "T23:59:59").getTime();
    if (start >= end) return [];

    const count = targetCount;
    const times: string[] = [];

    if (fillMode === "random") {
      // 生成带秒级随机的时间戳，避免同一天内分秒也完全相同
      for (let i = 0; i < count; i++) {
        const ts = randomBetween(start, end);
        const date = new Date(ts);
        // 在原分钟基础上，随机添加 0-59 秒
        date.setSeconds(randomBetween(0, 59));
        times.push(formatDateTime(date));
      }
      times.sort((a, b) => (a > b ? -1 : 1));
    } else {
      const step = Math.floor((end - start) / Math.max(count - 1, 1));
      for (let i = 0; i < count; i++) {
        const ts = start + step * i;
        const date = new Date(ts);
        // 在原分钟基础上，随机添加 0-59 秒，让顺序填充也有秒级差异
        date.setSeconds(randomBetween(0, 59));
        times.push(formatDateTime(date));
      }
      times.reverse();
    }
    return times;
  }

  function generatePublishers(): string[] {
    const pool =
      customPublishers.trim()
        ? customPublishers
            .split(/[\n,，]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : PUBLISHER_POOL;

    const count = pubTargetCount;
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      result.push(pool[randomBetween(0, pool.length - 1)]);
    }
    return result;
  }

  function handlePreviewTime() {
    const times = generateTimes();
    setTimePreview(times);
    setTimePreviewGenerated(true);
  }

  function handleApplyTime() {
    const times = generateTimes();
    const ids =
      applyScope === "selected" && selectedIds.length > 0
        ? selectedIds
        : [];
    onApplyTime(ids, times);
    onClose();
  }

  function handlePreviewPublisher() {
    const pubs = generatePublishers();
    setPubPreview(pubs);
    setPubPreviewGenerated(true);
  }

  function handleApplyPublisher() {
    const pubs = generatePublishers();
    const ids =
      pubScope === "selected" && selectedIds.length > 0
        ? selectedIds
        : [];
    onApplyPublisher(ids, pubs);
    onClose();
  }

  const isDateValid =
    startDate && endDate && new Date(startDate) < new Date(endDate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800">批量工具</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          <button
            onClick={() => setActiveTab("time")}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "time"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="ri-time-line mr-1.5" />
            批量替换发布时间
          </button>
          <button
            onClick={() => setActiveTab("publisher")}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "publisher"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="ri-user-line mr-1.5" />
            随机生成发布者
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {activeTab === "time" && (
            <>
              {/* Date Range */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">时间范围</label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setTimePreviewGenerated(false); }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                  <span className="text-gray-400 text-sm whitespace-nowrap">至</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setTimePreviewGenerated(false); }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
                {!isDateValid && startDate && endDate && (
                  <p className="text-xs text-red-500 mt-1">结束日期必须晚于开始日期</p>
                )}
              </div>

              {/* Fill Mode */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">填充方式</label>
                <div className="flex gap-3">
                  {[
                    { key: "random", label: "随机填充", desc: "在时间段内随机分布" },
                    { key: "sequential", label: "按序填充", desc: "均匀分布在时间段内" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      onClick={() => { setFillMode(m.key as "random" | "sequential"); setTimePreviewGenerated(false); }}
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-left transition-colors cursor-pointer ${
                        fillMode === m.key
                          ? "border-orange-400 bg-orange-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className={`text-sm font-medium ${fillMode === m.key ? "text-orange-600" : "text-gray-700"}`}>
                        {m.label}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Apply Scope */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">应用范围</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setApplyScope("all"); setTimePreviewGenerated(false); }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                      applyScope === "all"
                        ? "border-orange-400 bg-orange-50 text-orange-600"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    全部 ({totalCount} 条)
                  </button>
                  <button
                    onClick={() => { setApplyScope("selected"); setTimePreviewGenerated(false); }}
                    disabled={selectedIds.length === 0}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                      applyScope === "selected"
                        ? "border-orange-400 bg-orange-50 text-orange-600"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    已选中 ({selectedIds.length} 条)
                  </button>
                </div>
              </div>

              {/* Preview */}
              {timePreviewGenerated && timePreview.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    预览（前 5 条）
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    {timePreview.slice(0, 5).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="w-4 h-4 flex items-center justify-center bg-orange-100 text-orange-500 rounded-full text-[10px] font-medium">
                          {i + 1}
                        </span>
                        {t}
                      </div>
                    ))}
                    {timePreview.length > 5 && (
                      <div className="text-xs text-gray-400 pt-1">
                        ...共 {timePreview.length} 条
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "publisher" && (
            <>
              {/* Apply Scope */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">应用范围</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setPubScope("all"); setPubPreviewGenerated(false); }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                      pubScope === "all"
                        ? "border-orange-400 bg-orange-50 text-orange-600"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    全部 ({totalCount} 条)
                  </button>
                  <button
                    onClick={() => { setPubScope("selected"); setPubPreviewGenerated(false); }}
                    disabled={selectedIds.length === 0}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                      pubScope === "selected"
                        ? "border-orange-400 bg-orange-50 text-orange-600"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    已选中 ({selectedIds.length} 条)
                  </button>
                </div>
              </div>

              {/* Publisher Pool */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  自定义账户名池（可选）
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  每行或用逗号分隔一个账户名，留空则使用系统内置名称池（30个常见用户名）
                </p>
                <textarea
                  value={customPublishers}
                  onChange={(e) => { setCustomPublishers(e.target.value); setPubPreviewGenerated(false); }}
                  placeholder={"例如：\n用户A\n用户B, 用户C\n张三, 李四"}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none"
                />
              </div>

              {/* Built-in pool preview */}
              {!customPublishers.trim() && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">内置名称池预览</label>
                  <div className="flex flex-wrap gap-1.5">
                    {PUBLISHER_POOL.map((name) => (
                      <span key={name} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              {pubPreviewGenerated && pubPreview.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    预览（前 5 条）
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    {pubPreview.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="w-4 h-4 flex items-center justify-center bg-orange-100 text-orange-500 rounded-full text-[10px] font-medium">
                          {i + 1}
                        </span>
                        <i className="ri-user-line text-gray-400" />
                        {p}
                      </div>
                    ))}
                    {pubPreview.length > 5 && (
                      <div className="text-xs text-gray-400 pt-1">
                        ...共 {pubPreview.length} 条
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          {activeTab === "time" ? (
            <>
              <button
                onClick={handlePreviewTime}
                disabled={!isDateValid}
                className="px-4 py-2 text-sm border border-orange-400 text-orange-500 rounded-lg hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                预览效果
              </button>
              <button
                onClick={handleApplyTime}
                disabled={!isDateValid}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                确认应用
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handlePreviewPublisher}
                className="px-4 py-2 text-sm border border-orange-400 text-orange-500 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap"
              >
                预览效果
              </button>
              <button
                onClick={handleApplyPublisher}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap"
              >
                确认应用
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
